import "./styles.scss";

import { useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";

import { ReactComponent as Logo } from "@assets/logo/M_WHITE_LOGO_FULL.svg";

import { Text, Button, Icon } from "@fragments";
import {
  IconSparkles,
  IconArrowRight,
  IconPercentage,
  IconClockHour4,
  IconCoin,
  IconFileText,
  IconExternalLink,
  IconMail,
  IconPhoto,
  IconLogin2,
  IconAlertTriangle,
  IconLinkOff,
  IconCircleCheck,
  IconCircleX,
} from "@tabler/icons-react";

import { INVITE_STATE } from "../enums/inviteStates";

// Duration unit labels resolve at render; the unit id is the API value.
const durationUnitLabel = (t, unit) =>
  ({
    days: t("durationUnits.days"),
    weeks: t("durationUnits.weeks"),
    months: t("durationUnits.months"),
  })[unit] ?? "";

/**
 * Public mentoring invite (§6.9). A centered, shell-less landing: program banner,
 * name, description, terms and the gated accept/decline area — plus self-contained
 * status screens for the token (404/expired/used) and post-decline outcomes. No
 * app shell (it renders outside it, §10), so it carries its own brand + footer.
 */
export default function MentoringInviteView(props) {
  const { state } = props;

  if (state === INVITE_STATE.LOADING) {
    return (
      <InviteShell>
        <InviteSkeleton />
      </InviteShell>
    );
  }

  if (state === INVITE_STATE.VALID) {
    return (
      <InviteShell>
        <InviteCard {...props} />
      </InviteShell>
    );
  }

  return (
    <InviteShell>
      <InviteStatus state={state} />
    </InviteShell>
  );
}

// ── Page frame: brand mark · centered content · footer ─────────────────────────
function InviteShell({ children }) {
  const { t } = useTranslation("member");

  return (
    <div className="mentoring-invite">
      <header className="mi-brand">
        {/* SVG logo (currentColor) recoloured per theme via --surface-2, mirroring
            src/Screen's header/sidebar logos. */}
        <Logo className="mi-brand-logo" />
      </header>

      <main className="mi-main">{children}</main>

      <footer className="mi-foot">
        <Text.span contrast={5}>{t("invite.footer")}</Text.span>
      </footer>
    </div>
  );
}

// ── The live invite card (VALID) ───────────────────────────────────────────────
function InviteCard({
  program,
  invitedEmail,
  accountEmail,
  authResolved,
  isLoggedIn,
  emailMismatch,
  submitting,
  accept,
  decline,
  login,
  switchAccount,
}) {
  const { t } = useTranslation("member");

  const paragraphs = useMemo(
    () =>
      (program?.description || "")
        .split(/\n{2,}/)
        .map((p) => p.trim())
        .filter(Boolean),
    [program],
  );

  const splitPct = program?.split_percentage;
  const durationText = program?.duration_value
    ? `${program.duration_value} ${durationUnitLabel(t, program.duration_unit)}`.trim()
    : t("invite.terms.durationUndefined");
  const splitWindowText = program?.split_duration_value
    ? `${program.split_duration_value} ${durationUnitLabel(t, program.split_duration_unit)}`.trim()
    : null;

  return (
    <article className="mi-card">
      {/* Banner */}
      <div className="mi-banner" style={{ "--i": 0 }}>
        {program?.banner?.url ? (
          <img src={program.banner.url} alt={program?.name} loading="lazy" />
        ) : (
          <div className="mi-banner-fallback">
            <IconPhoto size={30} />
          </div>
        )}
      </div>

      <div className="mi-body">
        <span className="mi-eyebrow" style={{ "--i": 1 }}>
          <Text.span contrast={6} left={<IconSparkles size={14} />}>
            {t("invite.eyebrow")}
          </Text.span>
        </span>

        <div className="mi-title" style={{ "--i": 2 }}>
          <Text.h2 contrast={9} weight={500}>
            {program?.name}
          </Text.h2>
        </div>

        {paragraphs.length > 0 && (
          <div className="mi-desc" style={{ "--i": 3 }}>
            {paragraphs.map((p, i) => (
              <Text.p key={i} contrast={6} className="mi-desc-p">
                {p}
              </Text.p>
            ))}
          </div>
        )}

        {/* Terms */}
        <div className="mi-terms" style={{ "--i": 4 }}>
          <TermRow
            icon={<IconPercentage size={14} />}
            label={t("invite.terms.splitPercentage")}
            value={splitPct != null ? `${splitPct}%` : "—"}
            emphasis
          />
          <TermRow
            icon={<IconClockHour4 size={14} />}
            label={t("invite.terms.duration")}
            value={durationText}
          />
          {splitWindowText && (
            <TermRow
              icon={<IconCoin size={14} />}
              label={t("invite.terms.splitWindow")}
              value={splitWindowText}
            />
          )}

          {program?.contract_url && (
            <a
              className="mi-contract"
              href={program.contract_url}
              target="_blank"
              rel="noreferrer"
            >
              <Text.span
                contrast={7}
                left={<IconFileText size={15} />}
                right={<IconExternalLink size={13} />}
              >
                {t("invite.terms.viewContract")}
              </Text.span>
            </a>
          )}
        </div>

        {/* Who it's for — hidden on a mismatch so we never reveal the recipient
            address to someone signed in as a different account. */}
        {!emailMismatch && (
          <div className="mi-invited" style={{ "--i": 5 }}>
            <span className="mi-invited-icon">
              <IconMail size={15} />
            </span>
            <Text.span contrast={5}>
              <Trans
                t={t}
                i18nKey="invite.invitedTo"
                values={{ email: invitedEmail }}
                components={{ strong: <strong /> }}
              />
            </Text.span>
          </div>
        )}

        <div className="mi-divider" />

        {/* Gated actions */}
        <div className="mi-actions" style={{ "--i": 6 }}>
          {!authResolved ? (
            <div className="mi-checking">
              <span className="mi-spinner" aria-hidden="true" />
              <Text.span contrast={5}>{t("invite.checkingAccount")}</Text.span>
            </div>
          ) : !isLoggedIn ? (
            <>
              <Button
                text={t("invite.loginButton")}
                color="primary"
                size="large"
                startIcon={<IconLogin2 />}
                onClick={login}
                preventLoading
                fullWidth
              />
              <Text.span contrast={5} className="mi-hint">
                {t("invite.loginHint")}
              </Text.span>
            </>
          ) : emailMismatch ? (
            <div className="mi-mismatch">
              <div className="mi-mismatch-head">
                <span className="mi-mismatch-icon">
                  <IconAlertTriangle size={18} />
                </span>
                <div className="mi-mismatch-text">
                  <Text.span contrast={8} weight={500}>
                    {t("invite.mismatch.title")}
                  </Text.span>
                  <Text.span contrast={5}>
                    <Trans
                      t={t}
                      i18nKey="invite.mismatch.description"
                      values={{ email: accountEmail }}
                      components={{ strong: <strong /> }}
                    />
                  </Text.span>
                </div>
              </div>
              <Button
                text={t("invite.mismatch.switchAccount")}
                color="overlay"
                variant="rounded"
                size="small"
                onClick={switchAccount}
                preventLoading
                fullWidth
              />
            </div>
          ) : (
            <div className="mi-cta">
              <Button
                text={t("invite.accept")}
                color="primary"
                size="large"
                endIcon={<IconArrowRight size={18} />}
                onClick={accept}
                disabled={submitting}
                fullWidth
              />
              <Button
                text={t("invite.decline")}
                color="overlay"
                variant="rounded"
                size="large"
                onClick={decline}
                disabled={submitting}
                preventLoading
                fullWidth
              />
            </div>
          )}
        </div>
      </div>
    </article>
  );
}

function TermRow({ icon, label, value, emphasis }) {
  return (
    <div className="mi-term">
      <Text.span contrast={5} left={icon}>
        {label}
      </Text.span>
      <Text.span contrast={emphasis ? 9 : 8} weight={emphasis ? 500 : 300}>
        {value}
      </Text.span>
    </div>
  );
}

// ── Terminal / status screens (404 · expired · used · declined · error) ────────
// Copy is resolved at render so it follows the active language; the state keys
// come from INVITE_STATE and are never translated.
const getStatusCopy = (t) => ({
  [INVITE_STATE.NOT_FOUND]: {
    Icon: IconLinkOff,
    title: t("invite.status.notFound.title"),
    subtitle: t("invite.status.notFound.subtitle"),
    cta: { label: t("invite.status.notFound.cta"), href: "/" },
  },
  [INVITE_STATE.EXPIRED]: {
    Icon: IconAlertTriangle,
    title: t("invite.status.expired.title"),
    subtitle: t("invite.status.expired.subtitle"),
    cta: { label: t("invite.status.expired.cta"), href: "/" },
  },
  [INVITE_STATE.USED]: {
    Icon: IconCircleCheck,
    title: t("invite.status.used.title"),
    subtitle: t("invite.status.used.subtitle"),
    cta: { label: t("invite.status.used.cta"), href: "/mentoring" },
  },
  [INVITE_STATE.DECLINED]: {
    Icon: IconCircleX,
    title: t("invite.status.declined.title"),
    subtitle: t("invite.status.declined.subtitle"),
    cta: { label: t("invite.status.declined.cta"), href: "/" },
  },
  [INVITE_STATE.ERROR]: {
    Icon: IconAlertTriangle,
    title: t("invite.status.error.title"),
    subtitle: t("invite.status.error.subtitle"),
    cta: { label: t("invite.status.error.cta"), reload: true },
  },
});

function InviteStatus({ state }) {
  const { t } = useTranslation("member");
  const statusCopy = useMemo(() => getStatusCopy(t), [t]);

  const copy = statusCopy[state] ?? statusCopy[INVITE_STATE.ERROR];
  const { Icon: StatusIcon, title, subtitle, cta } = copy;

  function onCta() {
    if (cta?.reload) window.location.reload();
    else if (cta?.href) window.location.href = cta.href;
  }

  return (
    <article className="mi-card mi-card--status">
      <div className="mi-status">
        <Icon.Sphere size={76}>
          <StatusIcon />
        </Icon.Sphere>
        <Text.h3 contrast={8} weight={500}>
          {title}
        </Text.h3>
        <Text.span contrast={5} className="mi-status-sub">
          {subtitle}
        </Text.span>
        {cta && (
          <Button
            text={cta.label}
            color="inverse"
            variant="rounded"
            onClick={onCta}
            preventLoading
          />
        )}
      </div>
    </article>
  );
}

// ── Loading skeleton ───────────────────────────────────────────────────────────
function InviteSkeleton() {
  return (
    <article className="mi-card is-skeleton" aria-busy="true">
      <div className="mi-banner skel" />
      <div className="mi-body">
        <div className="skel skel-line" style={{ width: "40%", height: 20 }} />
        <div className="skel skel-line" style={{ width: "75%", height: 28 }} />
        <div className="skel skel-line" style={{ width: "100%", height: 60 }} />
        <div className="skel skel-line" style={{ width: "100%", height: 120 }} />
        <div className="skel skel-line" style={{ width: "100%", height: 48 }} />
      </div>
    </article>
  );
}
